export interface BadgeCellValue {
  text: string | null
  badge: string
}

export interface WindowCell {
  label: string
  volume: number | string
  isPeak: boolean
}

const NUMERIC = /^-?\d+(?:\.\d+)?$/u

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export function readBadgeCell(value: unknown): BadgeCellValue {
  const source = record(value)
  if (source === null) {
    return { text: null, badge: value === null || value === undefined ? '' : String(value) }
  }
  const text = typeof source['month'] === 'string' ? source['month'] : null
  return { text: text ?? '-', badge: formatBadge(source['lift']) }
}

function formatBadge(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'number') return `${value}x`
  const text = String(value)
  return NUMERIC.test(text) ? `${text}x` : text
}

export function readWindowCells(value: unknown): WindowCell[] {
  if (!Array.isArray(value)) return []
  return value
    .slice(0, 3)
    .map(record)
    .filter((cell): cell is Record<string, unknown> => cell !== null)
    .map((cell) => ({
      label: typeof cell['label'] === 'string' ? cell['label'] : '-',
      volume: readVolume(cell['volume']),
      isPeak: cell['is_peak'] === true || cell['is_peak'] === 'true',
    }))
}

function readVolume(value: unknown): number | string {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'number') return value
  const text = String(value)
  return NUMERIC.test(text) ? Number(text) : text
}

export type PercentileBand = 'poor' | 'belowAvg' | 'average' | 'good' | 'excellent'

/** v3 `getBenchmarkCategoryStyle` boundaries (`OrphexTd.vue:432-440`) — a `p_*` field's percentile
 * score buckets into one of five bands, matching the same ranges commerce-analytics' creative
 * benchmark uses. Kept local rather than imported: `boundaries/dependencies` only allows crossing
 * into another feature through its `index.ts`, and this is five numeric ranges. */
const PERCENTILE_BANDS = [
  { id: 'poor', max: 19 },
  { id: 'belowAvg', max: 39 },
  { id: 'average', max: 59 },
  { id: 'good', max: 79 },
  { id: 'excellent', max: 100 },
] as const satisfies readonly { id: PercentileBand; max: number }[]

export function readPercentileBand(value: unknown): PercentileBand | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed)) return null
  const clamped = Math.max(0, Math.min(100, parsed))
  return (PERCENTILE_BANDS.find((band) => clamped <= band.max) ?? PERCENTILE_BANDS[0]).id
}

function mixHex(from: readonly number[], to: readonly number[], ratio: number): string {
  return `#${from
    .map((channel, index) => Math.round(channel + ((to[index] ?? channel) - channel) * ratio))
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`
}

/** v3 `getPercentageStyle` (`OrphexTd.vue:452-494`) — a continuous red→yellow→green gradient for
 * a `performance_score` column, as opposed to `p_*`'s five discrete bands above. */
export function performanceScoreColor(value: number): string {
  const clamped = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0))
  return clamped <= 30
    ? mixHex([234, 6, 6], [230, 220, 34], clamped / 30)
    : mixHex([230, 220, 34], [98, 175, 0], (clamped - 30) / 70)
}
